import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { contentPlannerService } from '../utils/apiService';
import { API_ENDPOINTS } from '../config/api';

const Section = styled.section`
  background: #fff;
  border-radius: var(--border-radius);
  box-shadow: var(--shadow);
  margin-top: 10px;
  padding: 20px;
`;

const Title = styled.h3`
  margin: 0 0 14px;
  color: var(--secondary-color);
`;

const FormGrid = styled.form`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px;
  margin-bottom: 16px;
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
`;

const Button = styled.button`
  border: none;
  border-radius: 8px;
  padding: 10px 12px;
  font-weight: 600;
  cursor: pointer;
  background: ${props => (props.$danger ? '#dc3545' : props.$secondary ? '#6c757d' : 'var(--primary-color)')};
  color: #fff;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Head = styled.th`
  text-align: left;
  padding: 10px 8px;
  color: #555;
`;

const Cell = styled.td`
  border-top: 1px solid #eee;
  padding: 10px 8px;
  vertical-align: middle;
`;

const PreviewMedia = styled.div`
  width: 100px;
  height: 140px;
  border-radius: 8px;
  overflow: hidden;
  background: #f2f2f2;
  border: 1px solid #e6e6e6;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Msg = styled.p`
  color: ${props => (props.$error ? '#dc3545' : '#1f8a3b')};
  margin: 8px 0;
`;

const emptyForm = {
  title: '',
  caption: '',
  orientation: 'vertical',
  isActive: '1',
  sortOrder: '0',
  file: null,
  poster: null
};

const ContentPlannerSection = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const sortedItems = useMemo(
    () => [...items].sort((a, b) => a.sort_order - b.sort_order || b.id - a.id),
    [items]
  );

  const fetchItems = async () => {
    setLoading(true);
    try {
      const data = await contentPlannerService.getItems();
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || 'İçerikler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const clearMsg = () => {
    setError('');
    setSuccess('');
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    clearMsg();
    if (!form.file) {
      setError('Lütfen bir medya dosyası seçin.');
      return;
    }

    const fd = new FormData();
    fd.append('title', form.title.trim());
    fd.append('caption', form.caption.trim());
    fd.append('orientation', form.orientation);
    fd.append('isActive', form.isActive);
    fd.append('sortOrder', form.sortOrder || '0');
    fd.append('file', form.file);
    if (form.poster) fd.append('poster', form.poster);

    setSaving(true);
    try {
      await contentPlannerService.createItem(fd);
      setSuccess('İçerik eklendi.');
      setForm(emptyForm);
      await fetchItems();
    } catch (e2) {
      setError(e2.message || 'Kayıt başarısız.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (item) => {
    clearMsg();
    const fd = new FormData();
    fd.append('title', item.title);
    fd.append('caption', item.caption || '');
    fd.append('orientation', item.orientation);
    fd.append('sortOrder', String(item.sort_order));
    fd.append('isActive', item.is_active ? '0' : '1');
    try {
      await contentPlannerService.updateItem(item.id, fd);
      await fetchItems();
    } catch (e) {
      setError(e.message || 'Durum güncellenemedi.');
    }
  };

  const removeItem = async (id) => {
    clearMsg();
    if (!window.confirm('İçerik silinsin mi?')) return;
    try {
      await contentPlannerService.deleteItem(id);
      await fetchItems();
    } catch (e) {
      setError(e.message || 'Silme başarısız.');
    }
  };

  return (
    <Section>
      <Title>İçerik Planlayıcı (Görsel / Dikey / Yatay Video)</Title>
      <FormGrid onSubmit={handleCreate}>
        <Input placeholder="Başlık" required value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} />
        <Input placeholder="Açıklama" value={form.caption} onChange={(e) => setForm((p) => ({ ...p, caption: e.target.value }))} />
        <Select value={form.orientation} onChange={(e) => setForm((p) => ({ ...p, orientation: e.target.value }))}>
          <option value="vertical">Dikey</option>
          <option value="horizontal">Yatay</option>
          <option value="square">Kare</option>
        </Select>
        <Select value={form.isActive} onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.value }))}>
          <option value="1">Yayında</option>
          <option value="0">Pasif</option>
        </Select>
        <Input type="number" value={form.sortOrder} onChange={(e) => setForm((p) => ({ ...p, sortOrder: e.target.value }))} />
        <Input type="file" required accept="image/*,video/*" onChange={(e) => setForm((p) => ({ ...p, file: e.target.files?.[0] || null }))} />
        <Input type="file" accept="image/*" onChange={(e) => setForm((p) => ({ ...p, poster: e.target.files?.[0] || null }))} />
        <Button type="submit">{saving ? 'Kaydediliyor...' : 'İçerik Ekle'}</Button>
      </FormGrid>

      {error && <Msg $error>{error}</Msg>}
      {success && <Msg>{success}</Msg>}

      {loading ? (
        <p>Yükleniyor...</p>
      ) : (
        <Table>
          <thead>
            <tr>
              <Head>Başlık</Head>
              <Head>Tip</Head>
              <Head>Yön</Head>
              <Head>Durum</Head>
              <Head>Önizleme</Head>
              <Head>İşlem</Head>
            </tr>
          </thead>
          <tbody>
            {sortedItems.map((item) => (
              <tr key={item.id}>
                <Cell>{item.title}</Cell>
                <Cell>{item.media_type}</Cell>
                <Cell>{item.orientation}</Cell>
                <Cell>{item.is_active ? 'Yayında' : 'Pasif'}</Cell>
                <Cell>
                  <PreviewMedia>
                    {item.media_type === 'video' ? (
                      <video
                        src={API_ENDPOINTS.UPLOADS(item.file_url)}
                        poster={item.poster_url ? API_ENDPOINTS.UPLOADS(item.poster_url) : undefined}
                        controls
                        muted
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <img
                        src={API_ENDPOINTS.UPLOADS(item.file_url)}
                        alt={item.title}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    )}
                  </PreviewMedia>
                  <div style={{ marginTop: 6 }}>
                    <a href={API_ENDPOINTS.UPLOADS(item.file_url)} target="_blank" rel="noreferrer">Dosyayı Aç</a>
                  </div>
                </Cell>
                <Cell style={{ display: 'flex', gap: 8 }}>
                  <Button type="button" $secondary onClick={() => toggleActive(item)}>
                    {item.is_active ? 'Pasif Yap' : 'Aktif Yap'}
                  </Button>
                  <Button type="button" $danger onClick={() => removeItem(item.id)}>Sil</Button>
                </Cell>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Section>
  );
};

export default ContentPlannerSection;

